import React, { useLayoutEffect, useState } from 'react'
import { AdCreative, FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { CreativeMedia } from '../../creatives/CreativeCard/CreativeMedia'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { CreativePreview, getIFrameSource } from 'components/ad-preview'
import { useFetchCreativePreview } from 'hooks/creatives/fetch-creative-preview'
import { useAdCreativeReview } from 'hooks/api/useAdCreativeReview'
import { StatusPill } from './StatusPill'
import {
  PacketModalShell,
  modalGhostButtonStyle,
  modalInputStyle,
  modalPrimaryButtonStyle,
} from '../components/PacketModalShell'
import { light, radius, shadow, status as statusColors, type as typo } from '../tokens'

type Props = {
  businessID: number
  creative: AdCreative
  /** Pre-opens the reason form — used when the caller clicked Reject on the card. */
  startInReject?: boolean
  hasPrev: boolean
  hasNext: boolean
  onPrev: () => void
  onNext: () => void
  /** Called after a successful approve/reject — moves to the next ad, or closes if this was the last one. */
  onAdvance: () => void
  onClose: () => void
}

const navButtonStyle = (side: 'left' | 'right', disabled: boolean): React.CSSProperties => ({
  position: 'fixed',
  top: '50%',
  [side]: 24,
  transform: 'translateY(-50%)',
  zIndex: 1400,
  width: 48,
  height: 48,
  borderRadius: '50%',
  border: `1px solid ${light.hairline}`,
  background: light.surface,
  color: light.text,
  fontSize: 22,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  boxShadow: shadow.overlay,
})

const ctaLabel = (value?: string) =>
  value
    ? value
        .split('_')
        .map(word => word[0] + word.slice(1).toLowerCase())
        .join(' ')
    : undefined

/** One label/value row in the metadata panel; renders nothing when value is empty. */
const MetaRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div
      style={{
        fontFamily: typo.family.sans,
        fontSize: typo.size.eyebrow,
        fontWeight: typo.weight.semibold,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: light.textFaint,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: typo.family.sans,
        fontSize: typo.size.body,
        color: light.text,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  </div>
)

/**
 * The client's full-screen-ish view of one ad: media at a real size beside
 * every piece of copy Facebook will show (primary text, headline,
 * description, CTA, destination), plus the same approve/reject actions as
 * the card. Rejecting reveals an inline reason field here instead of
 * stacking a second modal on top. A successful decision advances to the next
 * ad (slideshow-style) rather than closing — the parent decides what "next"
 * means and closes instead once there isn't one.
 */
export function ReviewCreativePreviewModal({
  businessID,
  creative,
  startInReject = false,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onAdvance,
  onClose,
}: Props) {
  const [rejecting, setRejecting] = useState(startInReject)
  const [reason, setReason] = useState('')
  const { submitReview } = useAdCreativeReview(businessID, creative.id)
  const submitting = submitReview.isLoading

  // Dynamic creatives have no single "the ad" to preview live from Facebook —
  // same split the AE editor's `CreativePreviewModal` makes — so they keep the
  // plain asset thumbnail below.
  const isDynamic = isDynamicCreative(creative)

  const { refreshPreview, adCreativePreview, loadingPreview, refreshing, freshPreviewReady } =
    useFetchCreativePreview(creative.id, FBAdPreviewFormat.MOBILE_FEED_STANDARD, {
      // The inline "unavailable" banner from `CreativePreview` replaces the
      // hook's own error toast here, same as the editor's modal.
      suppressErrorToast: true,
    })

  // Force a live refresh on mount so the client is always deciding from the
  // current ad, not a stale hover-prefetched or previously-cached render. The
  // parent remounts this modal (`key={creative.id}`) on every prev/next/advance,
  // so a mount-only effect is enough to refresh per ad.
  useLayoutEffect(() => {
    if (isDynamic) return
    refreshPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDynamic])

  const fbFresh = freshPreviewReady && !loadingPreview && !refreshing
  const iframeSource = fbFresh && adCreativePreview ? getIFrameSource(adCreativePreview) : ''

  const destination = creative.destinationURL ?? creative.linkWebsiteURL
  const trimmedReason = reason.trim()

  const handleApprove = async () => {
    await submitReview.mutateAsync({ approved: true })
    onAdvance()
  }

  const handleSubmitReject = async () => {
    if (!trimmedReason || submitting) return
    await submitReview.mutateAsync({ approved: false, notes: trimmedReason })
    onAdvance()
  }

  return (
    <>
      <button
        type="button"
        aria-label="Previous ad"
        disabled={!hasPrev}
        onClick={onPrev}
        style={navButtonStyle('left', !hasPrev)}
      >
        ‹
      </button>
      <button
        type="button"
        aria-label="Next ad"
        disabled={!hasNext}
        onClick={onNext}
        style={navButtonStyle('right', !hasNext)}
      >
        ›
      </button>
      <PacketModalShell
        palette={light}
        hideHeader
        onClose={onClose}
        maxWidth={960}
        closeOnBackdropClick
      >
        <div style={{ position: 'relative', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: `1px solid ${light.hairline}`,
              background: light.surface,
              color: light.textMuted,
              cursor: 'pointer',
              zIndex: 1,
            }}
          >
            <i className="fa fa-times" />
          </button>

          <div
            style={{
              position: 'relative',
              flex: '1 1 420px',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: light.bg,
              border: `1px solid ${light.hairline}`,
              borderRadius: radius.md,
              padding: isDynamic ? 0 : 16,
              maxHeight: isDynamic ? 560 : undefined,
              overflow: isDynamic ? 'hidden' : 'visible',
            }}
          >
            <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1 }}>
              <StatusPill creative={creative} />
            </div>
            {isDynamic ? (
              <CreativeMedia
                creative={creative}
                className="w-full max-h-[560px]"
                objectFit="contain"
              />
            ) : (
              // Same live-Facebook-preview-over-local-fallback surface as the AE
              // editor's `CreativePreviewModal` (`components/ad-preview`'s
              // `CreativePreview`), so the client is looking at the same ad the
              // AE approved from, not just its raw asset.
              <CreativePreview
                creative={creative}
                iframeSource={iframeSource}
                loading={loadingPreview}
                refreshing={refreshing}
                onRetry={() => refreshPreview()}
                width={360}
                height="min(560px, 60vh)"
                frameless
              />
            )}
          </div>

          <div
            style={{
              flex: '1 1 280px',
              minWidth: 260,
              maxHeight: 560,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {creative.primaryText && <MetaRow label="Primary text">{creative.primaryText}</MetaRow>}
            {creative.linkHeadline && <MetaRow label="Headline">{creative.linkHeadline}</MetaRow>}
            {creative.linkDescription && (
              <MetaRow label="Description">{creative.linkDescription}</MetaRow>
            )}
            {creative.justification && (
              <MetaRow label="Why this ad">{creative.justification}</MetaRow>
            )}
            {ctaLabel(creative.callToAction) && (
              <MetaRow label="Call to action">{ctaLabel(creative.callToAction)}</MetaRow>
            )}
            {destination && (
              <MetaRow label="Destination">
                <a
                  href={destination}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: light.accent, wordBreak: 'break-all' }}
                >
                  {creative.linkDisplayURL ?? destination}
                </a>
              </MetaRow>
            )}

            <div style={{ flex: 1 }} />

            {rejecting ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label
                  style={{
                    fontFamily: typo.family.sans,
                    fontSize: typo.size.body,
                    fontWeight: typo.weight.semibold,
                    color: light.text,
                  }}
                >
                  Reason *
                </label>
                <textarea
                  autoFocus
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  placeholder="What should change?"
                  style={{ ...modalInputStyle(light), resize: 'none' }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setRejecting(false)}
                    disabled={submitting}
                    style={modalGhostButtonStyle(light)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitReject}
                    disabled={!trimmedReason || submitting}
                    style={{
                      ...modalPrimaryButtonStyle(light),
                      background: statusColors.danger,
                      borderColor: statusColors.danger,
                      opacity: !trimmedReason || submitting ? 0.6 : 1,
                      cursor: !trimmedReason || submitting ? 'default' : 'pointer',
                    }}
                  >
                    Submit rejection
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={submitting}
                  style={{
                    ...modalPrimaryButtonStyle(light),
                    background: statusColors.success,
                    borderColor: statusColors.success,
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setRejecting(true)}
                  disabled={submitting}
                  style={{
                    ...modalGhostButtonStyle(light),
                    color: statusColors.danger,
                    borderColor: statusColors.danger,
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        </div>
      </PacketModalShell>
    </>
  )
}
